/**
 * Management API client for interacting with Delta-Base event stores.
 *
 * This client provides operations for creating, listing, retrieving, updating,
 * and deleting event stores in the DeltaBase platform.
 */
import type { HttpClient } from '../utils/http';

/**
 * Event store information returned from the Management API.
 * Contains basic information about an event store.
 */
export interface EventStore {
  /**
   * Unique identifier for the event store
   */
  id: string;

  /**
   * Display name of the event store
   */
  name: string;

  /**
   * Optional description of the event store's purpose or contents
   */
  description?: string;

  /**
   * Optional cloud region where the event store is located
   */
  region?: string;

  /**
   * ISO timestamp of when the event store was created
   */
  createdAt: string;

  /**
   * ISO timestamp of when the event store was last updated
   */
  updatedAt: string;

  /**
   * Current status of the event store
   */
  status: 'active' | 'suspended' | 'deleted';
}

/**
 * Response from listing event stores.
 * Contains an array of event stores and their total count.
 */
export interface EventStoreListResponse {
  /**
   * Array of event stores returned from the listing operation
   */
  eventStores: EventStore[];

  /**
   * Total number of event stores available (for pagination)
   */
  totalCount: number;
}

/**
 * Detailed event store information including usage statistics and settings.
 * Extends the basic EventStore interface with additional details.
 */
export interface EventStoreDetailsResponse extends EventStore {
  /**
   * Usage statistics for the event store
   */
  statistics?: {
    /**
     * Total number of event streams stored
     */
    totalStreams: number;

    /**
     * Total number of events stored
     */
    totalEvents: number;

    /**
     * Storage size in bytes used by the event store
     */
    databaseSizeBytes: number;

    /**
     * ISO timestamp of the oldest event in the store, if available
     */
    oldestEventTimestamp?: string;

    /**
     * ISO timestamp of the newest event in the store, if available
     */
    newestEventTimestamp?: string;
  };

  /**
   * Configuration settings for the event store
   */
  settings?: {
    /**
     * Number of days events are retained before automatic deletion
     */
    retentionPeriodDays: number;

    /**
     * Maximum size in bytes allowed per event stream
     */
    maxStreamSizeBytes: number;
  };
}

/**
 * Options for creating a new event store.
 */
export interface CreateEventStoreOptions {
  /**
   * Display name of the event store (required) - should be between 3 and 64 characters long and can only contain lowercase letters, numbers, and hyphens
   */
  name: string;

  /**
   * Optional description of the event store's purpose or contents
   */
  description?: string;

  /**
   * Optional cloud region where the event store should be created
   */
  region?: string;

  /**
   * Optional configuration settings for the event store
   */
  settings?: {
    /**
     * Number of days to retain events before automatic deletion
     */
    retentionPeriodDays?: number;

    /**
     * Maximum size in bytes allowed per event stream
     */
    maxStreamSizeBytes?: number;
  };
}

/**
 * Management client for creating and managing event stores.
 *
 * Provides operations for the lifecycle of event stores including:
 * - Creating new event stores
 * - Listing available event stores
 * - Getting details about specific event stores
 * - Updating event store settings
 * - Deleting event stores
 */
export class ManagementClient {
  private http: HttpClient;

  /**
   * Creates a new ManagementClient instance.
   *
   * @param http - The HTTP client to use for API requests
   */
  constructor(http: HttpClient) {
    this.http = http;
  }

  /**
   * Creates a new event store in the DeltaBase platform.
   *
   * @param options - Configuration for the new event store
   * @returns Promise resolving to the created event store information
   * @throws {EventStoreAlreadyExistsError} When an event store with the same name exists
   * @throws {ValidationError} When request validation fails
   * @throws {AuthenticationError} When authentication fails
   * @throws {InternalServerError} When the server encounters an error
   *
   * @example
   * ```typescript
   * import {
   *   isEventStoreAlreadyExistsError,
   *   isValidationError
   * } from '@delta-base/server';
   *
   * try {
   *   const eventStore = await managementClient.createEventStore({
   *     name: 'orders-production',
   *     description: 'Production event store for order events',
   *     settings: {
   *       retentionPeriodDays: 90,
   *       maxStreamSizeBytes: 1073741824  // 1GB
   *     }
   *   });
   * } catch (error) {
   *   if (isEventStoreAlreadyExistsError(error)) {
   *     console.log(`Event store '${error.name}' already exists`);
   *     // Handle existing event store
   *   } else if (isValidationError(error)) {
   *     console.log('Validation errors:', error.validationErrors);
   *     // Handle validation failures
   *   } else {
   *     throw error; // Re-throw unknown errors
   *   }
   * }
   * ```
   */
  async createEventStore(
    options: CreateEventStoreOptions
  ): Promise<EventStore> {
    // API response schema - what the API actually returns
    interface ApiEventStoreResponse {
      eventStoreId: string;
      eventStoreName: string;
      eventStoreDurableObjectId: string;
      eventBusDurableObjectId: string;
      created: string;
      description?: string;
      settings?: {
        retentionPeriodDays?: number;
        maxStreamSizeBytes?: number;
      };
    }

    const apiResponse = await this.http.post<ApiEventStoreResponse>(
      '/api/event-stores',
      options as unknown as Record<string, unknown>
    );

    // Map API response to client interface
    return {
      id: apiResponse.eventStoreId,
      name: apiResponse.eventStoreName,
      description: apiResponse.description,
      region: undefined, // API doesn't return region currently
      createdAt: apiResponse.created,
      updatedAt: apiResponse.created, // API doesn't return updatedAt, use created as fallback
      status: 'active' as const, // API doesn't return status, assume active for new stores
    };
  }

  /**
   * Retrieves a list of all event stores accessible to the current user.
   *
   * @returns Promise resolving to a list of event stores and total count
   *
   * @example
   * ```typescript
   * // Get all available event stores
   * const { eventStores, totalCount } = await managementClient.listEventStores();
   *
   * // Display event store names
   * eventStores.forEach(store => {
   *   console.log(`${store.name} (${store.id}): ${store.status}`);
   * });
   * ```
   */
  async listEventStores(): Promise<EventStoreListResponse> {
    // API response schema - what the API actually returns
    interface ApiEventStoreListResponse {
      eventStores: {
        eventStoreId: string;
        eventStoreName: string;
        eventStoreDurableObjectId: string;
        eventBusDurableObjectId: string;
        created: string;
        description?: string;
        settings?: {
          retentionPeriodDays?: number;
          maxStreamSizeBytes?: number;
        };
      }[];
      totalCount: number;
    }

    const apiResponse =
      await this.http.get<ApiEventStoreListResponse>('/api/event-stores');

    // Map API response to client interface
    return {
      eventStores: apiResponse.eventStores.map((store) => ({
        id: store.eventStoreId,
        name: store.eventStoreName,
        description: store.description,
        region: undefined, // API doesn't return region currently
        createdAt: store.created,
        updatedAt: store.created, // API doesn't return updatedAt, use created as fallback
        status: 'active' as const, // API doesn't return status, assume active
      })),
      totalCount: apiResponse.totalCount,
    };
  }

  /**
   * Retrieves detailed information about a specific event store.
   *
   * @param eventStoreName - Name of the event store to retrieve
   * @returns Promise resolving to detailed information about the event store
   *
   * @example
   * ```typescript
   * // Get details for a specific event store
   * const eventStore = await managementClient.getEventStore('es_12345');
   *
   * // Access statistics
   * if (eventStore.statistics) {
   *   console.log(`Total events: ${eventStore.statistics.totalEvents}`);
   *   console.log(`Storage used: ${eventStore.statistics.databaseSizeBytes} bytes`);
   * }
   * ```
   */
  async getEventStore(
    eventStoreName: string
  ): Promise<EventStoreDetailsResponse> {
    // API response schema - what the API actually returns
    interface ApiEventStoreDetailsResponse {
      eventStoreId: string;
      eventStoreName: string;
      eventStoreDurableObjectId: string;
      eventBusDurableObjectId: string;
      created: string;
      description?: string;
      settings?: {
        retentionPeriodDays?: number;
        maxStreamSizeBytes?: number;
      };
      statistics?: {
        totalStreams: number;
        totalEvents: number;
        databaseSizeBytes: number;
        oldestEventTimestamp?: string;
        newestEventTimestamp?: string;
      };
    }

    const apiResponse = await this.http.get<ApiEventStoreDetailsResponse>(
      `/api/event-stores/${eventStoreName}`
    );

    // Map API response to client interface
    return {
      id: apiResponse.eventStoreId,
      name: apiResponse.eventStoreName,
      description: apiResponse.description,
      region: undefined, // API doesn't return region currently
      createdAt: apiResponse.created,
      updatedAt: apiResponse.created, // API doesn't return updatedAt, use created as fallback
      status: 'active' as const, // API doesn't return status, assume active
      statistics: apiResponse.statistics,
      settings:
        apiResponse.settings &&
        apiResponse.settings.retentionPeriodDays !== undefined &&
        apiResponse.settings.maxStreamSizeBytes !== undefined
          ? {
              retentionPeriodDays: apiResponse.settings.retentionPeriodDays,
              maxStreamSizeBytes: apiResponse.settings.maxStreamSizeBytes,
            }
          : undefined,
    };
  }

  /**
   * Permanently deletes an event store and all its data.
   *
   * This operation cannot be undone. All event streams, events, and
   * subscriptions associated with this event store will be deleted.
   *
   * @param eventStoreName - Name of the event store to delete
   * @returns Promise that resolves when the deletion is complete
   *
   * @example
   * ```typescript
   * // Delete an event store
   * await managementClient.deleteEventStore('es_12345');
   * ```
   */
  async deleteEventStore(eventStoreName: string): Promise<void> {
    await this.http.delete<void>(`/api/event-stores/${eventStoreName}`);
  }

  /**
   * Updates settings for an existing event store.
   *
   * @param eventStoreName - ID of the event store to update
   * @param settings - New settings for the event store
   * @param settings.description - Optional new description for the event store
   * @param settings.retentionPeriodDays - Optional new retention period in days
   * @param settings.maxStreamSizeBytes - Optional new maximum stream size in bytes
   * @returns Promise resolving to the updated event store information
   *
   * @example
   * ```typescript
   * // Update event store description
   * const updated = await managementClient.updateEventStore('es_12345', {
   *   description: 'Updated description for this event store'
   * });
   *
   * // Update event store retention settings
   * const updated = await managementClient.updateEventStore('es_12345', {
   *   retentionPeriodDays: 180,  // Keep events for 6 months
   *   maxStreamSizeBytes: 536870912  // 512MB per stream
   * });
   * ```
   */
  async updateEventStore(
    eventStoreName: string,
    settings: {
      description?: string;
      retentionPeriodDays?: number;
      maxStreamSizeBytes?: number;
    }
  ): Promise<EventStore> {
    // API response schema - what the API actually returns
    interface ApiEventStoreResponse {
      eventStoreId: string;
      eventStoreName: string;
      eventStoreDurableObjectId: string;
      eventBusDurableObjectId: string;
      created: string;
      description?: string;
      settings?: {
        retentionPeriodDays?: number;
        maxStreamSizeBytes?: number;
      };
    }

    const apiResponse = await this.http.put<ApiEventStoreResponse>(
      `/api/event-stores/${eventStoreName}`,
      settings
    );

    // Map API response to client interface
    return {
      id: apiResponse.eventStoreId,
      name: apiResponse.eventStoreName,
      description: apiResponse.description,
      region: undefined, // API doesn't return region currently
      createdAt: apiResponse.created,
      updatedAt: apiResponse.created, // API doesn't return updatedAt, use created as fallback
      status: 'active' as const, // API doesn't return status, assume active
    };
  }
}
